import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { Paginated } from "@/@types/generics/paginated";
import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { GenericObject } from "@/@types/generics/genericals";
import { getAge } from "@/utils/dateUtil";
import { DetailedCitizenAttendancesIndicatorC7Data } from "@/services/esus/indicator/esf/c7/getDetailedCitizensAttendancesIndicatorC7";
import { getCitizenScoreStatusDescriptionIndicatorC7 } from "@/utils/indicatorsEsf";

import { IndicatorEsfC7CitizensAttendancesFilters } from "./IndicatorEsfC7CitizensAttendancesFilters";
import { IndicatorEsfC7CitizensAttendancesTable } from "./IndicatorEsfC7CitizensAttendancesTable";
import { IndicatorEsfC7CitizenAttendancesHistory } from "./IndicatorEsfC7CitizenAttendancesHistory";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEsfC7CitizensAttendancesProps = {
	selectedQuarter: string;
	search: string;
	setSearch: (search: string) => void;
	isEvaluated: boolean | undefined;
	setIsEvaluated: (isEvaluated: boolean | undefined) => void;
	isLoading: boolean;
	paginatedAttendances: Paginated<DetailedCitizenAttendancesIndicatorC7Data>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllDetailedIndicatorC7: () => Promise<DetailedCitizenAttendancesIndicatorC7Data[]>;
};

export function IndicatorEsfC7CitizensAttendances({
	selectedQuarter,
	search,
	setSearch,
	isEvaluated,
	setIsEvaluated,
	isLoading,
	paginatedAttendances,
	pageNumber,
	setPageNumber,
	handleGetAllDetailedIndicatorC7: handleGetAllDetailedIndicatorC1,
}: Readonly<IndicatorEsfC7CitizensAttendancesProps>) {
	const [isCitizenHistoryModalOpen, setIsCitizenHistoryModalOpen] = useState(false);
	const [citizenWithAttendances, setCitizenWithAttendances] =
		useState<DetailedCitizenAttendancesIndicatorC7Data>(
			new DetailedCitizenAttendancesIndicatorC7Data()
		);
	const [isLoadingCitizenWithAttendances, setIsLoadingCitizenWithAttendances] = useState(false);
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);

	async function handleGetCitizenWithAttendances(
		citizenWithAttendances: DetailedCitizenAttendancesIndicatorC7Data
	) {
		setIsLoadingCitizenWithAttendances(true);
		setCitizenWithAttendances(citizenWithAttendances);
		setIsLoadingCitizenWithAttendances(false);
		setIsCitizenHistoryModalOpen(true);
	}

	async function handleExportTable() {
		setIsLoadingExportData(true);
		const attendances = await handleGetAllDetailedIndicatorC1();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(attendances).length > 0) {
			const excelData: GenericObject[] =
				transformIndicatorC1AttendancesToGenericObjectArray(attendances);
			exportExcel(
				excelData,
				`Prática C - Consultas - ${dayjs(quarterDates.startDate).format(
					"DD-MM-YYYY"
				)} à ${dayjs(quarterDates.endDate).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformIndicatorC1AttendancesToGenericObjectArray(
		citizensWithAttendances: DetailedCitizenAttendancesIndicatorC7Data[]
	) {
		return citizensWithAttendances.map(
			(citizenWithAttendances) =>
				({
					Nome: citizenWithAttendances.fullName,
					DN: citizenWithAttendances.birthdate
						? dayjs(citizenWithAttendances.birthdate).format("DD/MM/YYYY")
						: "",
					Idade: citizenWithAttendances.birthdate
						? getAge(citizenWithAttendances.birthdate)
						: "",
					Documento: citizenWithAttendances.cpf ?? citizenWithAttendances.cns ?? "",
					Area: citizenWithAttendances.area ?? "FA",
					"Ine Equipe": citizenWithAttendances.healthTeam?.ine ?? "",
					"Nome Equipe": citizenWithAttendances.healthTeam?.name ?? "",
					"Última Consulta": citizenWithAttendances.lastAttendance?.date
						? dayjs(citizenWithAttendances.lastAttendance.date).format("DD/MM/YYYY")
						: "",
					"Status última Consulta": getCitizenScoreStatusDescriptionIndicatorC7(
						citizenWithAttendances.lastAttendanceStatus
					),
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingCitizenWithAttendances || isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEsfC7CitizensAttendancesFilters
							search={search}
							setSearch={setSearch}
							isEvaluated={isEvaluated}
							setIsEvaluated={setIsEvaluated}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEsfC7CitizensAttendancesTable
							citizensWithAttendances={paginatedAttendances.data}
							isLoading={isLoading}
							openCitizenHistory={handleGetCitizenWithAttendances}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedAttendances?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedAttendances.pageSize}
								totalPages={paginatedAttendances.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
				<Modal
					show={isCitizenHistoryModalOpen}
					onHide={() => setIsCitizenHistoryModalOpen(false)}
					size="xl"
					aria-labelledby="contained-modal-title-vcenter"
					centered
				>
					<Modal.Header closeButton>
						<Modal.Title>
							<h3 className="text-uppercase text-dark fw-bold fs-7">
								Histórico de Atendimentos
							</h3>
						</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<IndicatorEsfC7CitizenAttendancesHistory
							citizenWithAttendances={citizenWithAttendances}
						/>
					</Modal.Body>
				</Modal>
			</Col>
		</Row>
	);
}
